import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './tenant-admin.scss';
import DataGrid, {
  DataGridRef,
  Column, Toolbar, Item, LoadPanel, Sorting,
  HeaderFilter, FilterRow, Scrolling, SearchPanel,
  DataGridTypes
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import { Popup, ToolbarItem } from 'devextreme-react/popup';
import Form, { SimpleItem, RequiredRule } from 'devextreme-react/form';
import notify from 'devextreme/ui/notify';
import { useAuth } from '../../contexts/auth';
import {
  TenantDto,
  listTenants,
  createTenant,
} from '../../api/tenants';

export const TenantAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const gridRef = useRef<DataGridRef>(null);

  // ── State ──────────────────────────────────────────────
  const [tenants, setTenants] = useState<TenantDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [createPopupVisible, setCreatePopupVisible] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    companyName: '',
    contactEmail: '',
    notes: '',
  });

  // ── Data loading ───────────────────────────────────────
  const loadTenants = useCallback(async() => {
    setLoading(true);
    try {
      const data = await listTenants(user?.token);
      setTenants(data);
    } catch (error: any) {
      notify(error?.response?.data || 'Fout bij laden van tenants', 'error', 3000);
    } finally {
      setLoading(false);
    }
  }, [user?.token]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // ── Row double-click → navigate to details ─────────────
  const onRowDblClick = useCallback(
    ({ data }: DataGridTypes.RowDblClickEvent) => {
      navigate('/tenant-details', { state: { tenantOid: data.Oid } });
    },
    [navigate]
  );

  // ── Create tenant ──────────────────────────────────────
  const onCreateClick = useCallback(() => {
    setCreateFormData({ name: '', companyName: '', contactEmail: '', notes: '' });
    setCreatePopupVisible(true);
  }, []);

  const onCreateSubmit = useCallback(async() => {
    if (!createFormData.name || !createFormData.companyName) {
      notify('Naam en bedrijfsnaam zijn verplicht', 'warning', 2000);
      return;
    }
    try {
      await createTenant(createFormData, user?.token);
      notify('Tenant succesvol aangemaakt', 'success', 2000);
      setCreatePopupVisible(false);
      await loadTenants();
    } catch (error: any) {
      const msg = error?.response?.data || 'Fout bij aanmaken tenant';
      notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error', 3000);
    }
  }, [createFormData, user?.token, loadTenants]);

  // ── Cell renderers ─────────────────────────────────────
  const statusCellRender = (data: DataGridTypes.ColumnCellTemplateData) => (
    <span className={`active-badge ${data.value ? 'active' : 'inactive'}`}>
      {data.value ? '✓ Actief' : '✗ Inactief'}
    </span>
  );

  const dateCellRender = (data: DataGridTypes.ColumnCellTemplateData) => {
    if (!data.value) return <span>—</span>;
    return <span>{new Date(data.value).toLocaleDateString('nl-NL')}</span>;
  };

  // ── Render ─────────────────────────────────────────────
  return (
    <div className='view tenant-admin'>
      <div className='view-wrapper'>
        <DataGrid
          className='grid'
          noDataText='Geen tenants gevonden'
          focusedRowEnabled
          height='100%'
          dataSource={tenants}
          keyExpr='Oid'
          onRowDblClick={onRowDblClick}
          allowColumnReordering
          ref={gridRef}
        >
          <LoadPanel enabled={loading} />
          <SearchPanel width={240} visible placeholder='Tenant zoeken...' />
          <HeaderFilter visible />
          <FilterRow visible />
          <Sorting mode='multiple' />
          <Scrolling mode='virtual' />
          <Toolbar>
            <Item location='before'>
              <div className='grid-header'>Tenant Beheer</div>
            </Item>
            <Item location='after' locateInMenu='auto'>
              <Button
                icon='plus'
                text='Nieuwe Tenant'
                type='default'
                stylingMode='contained'
                onClick={onCreateClick}
              />
            </Item>
            <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button icon='refresh' text='Vernieuwen' stylingMode='text' onClick={loadTenants} />
            </Item>
            <Item name='searchPanel' locateInMenu='auto' />
          </Toolbar>
          <Column dataField='CompanyName' caption='Bedrijfsnaam' sortOrder='asc' />
          <Column dataField='Name' caption='Tenant ID (domein)' />
          <Column dataField='ContactEmail' caption='Contact Email' />
          <Column dataField='IsActive' caption='Status' width={100} cellRender={statusCellRender} />
          <Column dataField='CreatedDate' caption='Aangemaakt' width={120} cellRender={dateCellRender} />
          <Column dataField='DeactivatedDate' caption='Gedeactiveerd' width={130} cellRender={dateCellRender} />
          <Column dataField='ConnectionString' caption='Database' visible={false} />
        </DataGrid>

        {/* ── Create Popup ──────────────────────────── */}
        <Popup
          visible={createPopupVisible}
          onHiding={() => setCreatePopupVisible(false)}
          title='Nieuwe Tenant Aanmaken'
          width={500}
          height='auto'
          showCloseButton
        >
          <ToolbarItem
            widget='dxButton'
            toolbar='bottom'
            location='after'
            options={{
              text: 'Aanmaken',
              type: 'default',
              stylingMode: 'contained',
              onClick: onCreateSubmit,
            }}
          />
          <ToolbarItem
            widget='dxButton'
            toolbar='bottom'
            location='after'
            options={{
              text: 'Annuleren',
              stylingMode: 'outlined',
              onClick: () => setCreatePopupVisible(false),
            }}
          />
          <Form formData={createFormData} labelMode='floating' colCount={1}>
            <SimpleItem dataField='name' label={{ text: 'Tenant Domein (bijv. bedrijf.nl)' }}>
              <RequiredRule message='Tenant domein is verplicht' />
            </SimpleItem>
            <SimpleItem dataField='companyName' label={{ text: 'Bedrijfsnaam' }}>
              <RequiredRule message='Bedrijfsnaam is verplicht' />
            </SimpleItem>
            <SimpleItem dataField='contactEmail' label={{ text: 'Contact Email' }} />
            <SimpleItem
              dataField='notes'
              editorType='dxTextArea'
              label={{ text: 'Notities' }}
              editorOptions={{ height: 80 }}
            />
          </Form>
        </Popup>
      </div>
    </div>
  );
};
